import { execForm, getApiToken } from '../utils';

export interface UploadCatalogFieldsResult {
  success: boolean;
  added: number;
  updated: number;
  deleted: number;
  unchanged: number;
  error?: string;
}

interface UploadCatalogFieldMappingsExecVariables {
  token: string;
  partnerCode: string;
  preview?: boolean;
}

interface UploadCatalogFieldMappingsExecResponse {
  restUser: {
    partner: {
      uploadCatalogFields: UploadCatalogFieldsResult;
    };
  };
}

/**
 * Uploads a TSV catalog field mapping file for a given partner.
 * The file contents will be persisted to the catalog_fields table.
 *
 * @param partnerCode - The partner code to upload the mapping for.
 * @param file - The TSV file to upload.
 * @param preview - If true, returns a dry-run report without persisting changes.
 * @returns A promise resolving with the upload result.
 */
export const uploadCatalogFieldMappings = async (
  partnerCode: string,
  file: File,
  preview = false,
): Promise<UploadCatalogFieldsResult> => {
  const apiToken = await getApiToken();

  const mutation = `
    mutation uploadCatalogFields(
      $partnerCode: String!
      $token: String!
      $preview: Boolean
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          uploadCatalogFields(preview: $preview) {
            success
            added
            updated
            deleted
            unchanged
            error
          }
        }
      }
    }
  `;

  const variables: UploadCatalogFieldMappingsExecVariables = {
    token: apiToken,
    partnerCode,
    preview,
  };

  const data = await execForm<UploadCatalogFieldMappingsExecVariables, UploadCatalogFieldMappingsExecResponse>(
    mutation,
    variables,
    { file },
  );

  return data.restUser.partner.uploadCatalogFields;
};
